import type {
  AuthTokensResponse,
  LoginResponse,
  LoginResult,
  MfaFactorType,
  OAuthProvidersResponse,
  OAuthProviderSlug,
  SessionContextResponse,
  TenantMembershipSummary,
} from '@tabita/contracts';
import { apiRequest, tokenStore, BASE_URL } from './api-client';

export const authApi = {
  /** The user, the active tenant, and the modules/permissions driving the shell. */
  me(signal?: AbortSignal): Promise<SessionContextResponse> {
    return apiRequest<SessionContextResponse>('/auth/me', { signal });
  },

  /**
   * Returns a `LoginResponse` with tokens already stored, or an
   * `mfaRequired` result carrying nothing to store — there is no session
   * yet, only proof the password was correct.
   */
  async login(email: string, password: string, rememberMe?: boolean): Promise<LoginResult> {
    const response = await apiRequest<LoginResult>('/auth/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    });

    if (!('mfaRequired' in response)) {
      tokenStore.set(response.accessToken, response.refreshToken, rememberMe);
    }
    return response;
  },

  async completeMfaChallenge(
    mfaToken: string,
    factorType: MfaFactorType,
    code: string,
    rememberMe?: boolean,
  ): Promise<LoginResponse> {
    const response = await apiRequest<LoginResponse>('/auth/mfa/challenge', {
      method: 'POST',
      body: { mfaToken, factorType, code },
      anonymous: true,
    });

    tokenStore.set(response.accessToken, response.refreshToken, rememberMe);
    return response;
  },

  sendMfaChallengeCode(mfaToken: string, factorType: 'EMAIL' | 'SMS'): Promise<void> {
    return apiRequest<void>('/auth/mfa/challenge/send', {
      method: 'POST',
      body: { mfaToken, factorType },
      anonymous: true,
    });
  },

  /**
   * The churches this user belongs to.
   *
   * Needed after a page reload: the token survives in sessionStorage but the
   * login response does not, and the selection screen has nothing to list.
   */
  listMemberships(): Promise<readonly TenantMembershipSummary[]> {
    return apiRequest<readonly TenantMembershipSummary[]>('/auth/memberships');
  },

  /**
   * Selects a tenant, exchanging the tenant-less token for one carrying the
   * church's permissions and scopes.
   */
  async selectTenant(tenantId: string): Promise<AuthTokensResponse> {
    const tokens = await apiRequest<AuthTokensResponse>('/auth/tenant', {
      method: 'POST',
      body: { tenantId },
    });

    tokenStore.set(tokens.accessToken, tokens.refreshToken);
    return tokens;
  },

  /**
   * Always resolves, whether or not the email belongs to an account — the
   * API responds identically either way, so there is nothing here for the
   * client to branch on.
   */
  requestPasswordReset(email: string): Promise<void> {
    return apiRequest<void>('/auth/password/reset-request', {
      method: 'POST',
      body: { email },
      anonymous: true,
    });
  },

  confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    return apiRequest<void>('/auth/password/reset-confirm', {
      method: 'POST',
      body: { token, newPassword },
      anonymous: true,
    });
  },

  verifyEmail(token: string): Promise<void> {
    return apiRequest<void>('/auth/verify-email', {
      method: 'POST',
      body: { token },
      anonymous: true,
    });
  },

  /** Which social sign-in buttons the server can actually complete a flow
   *  for — a provider missing its credentials is simply absent. */
  getOAuthProviders(): Promise<OAuthProvidersResponse> {
    return apiRequest<OAuthProvidersResponse>('/auth/oauth/providers', { anonymous: true });
  },

  /** Not a fetch — a browser navigation. The provider's own consent screen
   *  is a full page, not something an XHR response could ever represent. */
  oauthStartUrl(provider: OAuthProviderSlug, rememberMe: boolean): string {
    const url = new URL(`${BASE_URL}/auth/oauth/${provider}/start`, window.location.origin);
    if (rememberMe) url.searchParams.set('remember', '1');
    return url.toString();
  },

  /** Trades the one-time code the `/oauth-callback` redirect carried for
   *  the real `LoginResult` the callback already finished computing. */
  async oauthHandoff(code: string, rememberMe: boolean): Promise<LoginResult> {
    const result = await apiRequest<LoginResult>('/auth/oauth/handoff', {
      method: 'POST',
      body: { code },
      anonymous: true,
    });

    if (!('mfaRequired' in result)) {
      tokenStore.set(result.accessToken, result.refreshToken, rememberMe);
    }
    return result;
  },

  async logout(): Promise<void> {
    try {
      await apiRequest<void>('/auth/logout', { method: 'POST' });
    } finally {
      // Cleared even if the request fails. A user who clicked sign out must end
      // up signed out locally regardless of what the network did.
      tokenStore.clear();
    }
  },
};
